// client/src/components/admin/BidsManagement.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BidsManagement = () => {
  const [bids, setBids] = useState([]);
  const [filteredBids, setFilteredBids] = useState([]);
  const [activeTab, setActiveTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  useEffect(() => {
    const fetchBids = async () => {
      try {
        // Corrected endpoint based on server.js route configuration
        const adminBidsRes = await axios.get('/api/bids/admin/bids');
        
        if (adminBidsRes.data && adminBidsRes.data.success && Array.isArray(adminBidsRes.data.bids)) {
          const allBids = adminBidsRes.data.bids;
          
          // Sort bids by creation date, newest first
          allBids.sort((a, b) => {
            const dateA = new Date(a.createdAt || 0);
            const dateB = new Date(b.createdAt || 0);
            return dateB - dateA;
          });
          
          setBids(allBids);
          setFilteredBids(allBids);
          setLoading(false);
          return;
        }
        
        // If the first response format isn't as expected, try alternative formats
        if (adminBidsRes.data) {
          let allBids;
          
          if (Array.isArray(adminBidsRes.data)) {
            allBids = adminBidsRes.data;
          } else if (adminBidsRes.data.data && Array.isArray(adminBidsRes.data.data)) {
            allBids = adminBidsRes.data.data;
          } else {
            throw new Error('Unexpected response format from server');
          }
          
          // Sort bids
          allBids.sort((a, b) => {
            const dateA = new Date(a.createdAt || 0);
            const dateB = new Date(b.createdAt || 0);
            return dateB - dateA;
          });
          
          setBids(allBids);
          setFilteredBids(allBids);
          setLoading(false);
          return;
        }
        
        // If we reach here, something went wrong with the response format
        throw new Error('Invalid response format from server');
      } catch (err) {
        console.error('Error fetching bids:', err);
        setError('Failed to fetch bids. Please check your network connection and try again.');
        setLoading(false);
      }
    };
    
    fetchBids();
  }, []);
  
  useEffect(() => {
    // Filter bids based on active tab
    if (activeTab === 'all') {
      setFilteredBids(bids);
    } else {
      setFilteredBids(bids.filter(bid => bid.status === activeTab));
    }
  }, [activeTab, bids]);
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  // Helper function to format currency with currency code
  const formatCurrency = (amount, currency = 'EUR') => {
    if (amount === undefined || amount === null) return `${currency} 0`;
    return `${currency} ${(Number(amount) || 0).toLocaleString('en-US')}`;
  };
  
  if (loading) {
    return <div className="loading">Loading bids...</div>;
  }
  
  if (error) {
    return <div className="error-message">{error}</div>;
  }
  
  // If we didn't get any bids, show an informative message
  if (bids.length === 0) {
    return (
      <div className="admin-container">
        <h2 className="admin-page-title">Bid Management</h2>
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">No Bids Found</h3>
          </div>
          <div className="admin-card-body">
            <p>No bids have been submitted in the system yet. Once banks start submitting bids, they will appear here.</p>
            
            <div className="admin-section-title">Possible Solutions:</div>
            <ol>
              <li>Make sure there are active requests in the system that banks can bid on.</li>
              <li>Ensure that banks have submitted bids for requests.</li>
              <li>Check that your API endpoint for retrieving bids is working correctly.</li>
              <li>Verify that bids are being properly stored in your database.</li>
            </ol>
            
            <div className="admin-actions">
              <Link to="/admin/requests" className="btn btn-primary">
                View Liquidity Requests
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="admin-container">
      <h2 className="admin-page-title">Bid Management</h2>
      
      <div className="admin-tab-container">
        <div className="admin-tabs">
          <div 
            className={`admin-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => handleTabChange('all')}
          >
            All Bids
          </div>
          <div 
            className={`admin-tab ${activeTab === 'pending' ? 'active' : ''}`}
            onClick={() => handleTabChange('pending')}
          >
            Pending
          </div>
          <div 
            className={`admin-tab ${activeTab === 'approved' ? 'active' : ''}`}
            onClick={() => handleTabChange('approved')}
          >
            Approved
          </div>
          <div 
            className={`admin-tab ${activeTab === 'rejected' ? 'active' : ''}`}
            onClick={() => handleTabChange('rejected')}
          >
            Rejected
          </div>
          <div 
            className={`admin-tab ${activeTab === 'cancelled' ? 'active' : ''}`}
            onClick={() => handleTabChange('cancelled')}
          >
            Cancelled
          </div>
        </div>
      </div>
      
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Bids ({filteredBids.length})</h3>
        </div>
        <div className="admin-card-body">
          <div className="admin-table-responsive">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Bid ID</th>
                  <th>Request ID</th>
                  <th>Company</th>
                  <th>Bank</th>
                  <th>Bid Amount</th>
                  <th>Interest Rate</th>
                  <th>Submission Date</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredBids.length > 0 ? (
                  filteredBids.map(bid => (
                    <tr key={bid.id || bid._id}>
                      <td>#{bid.id || bid._id}</td>
                      <td>
                        {bid.request?.requestId ? (
                          <Link to={`/admin/requests/${bid.request.id || bid.request._id}`}>
                            #{bid.request.requestId}
                          </Link>
                        ) : 'N/A'}
                      </td>
                      <td>
                        {bid.request?.company?.name ? (
                          <Link to={`/admin/organizations/${bid.request.company._id || bid.request.company.id}`}>
                            {bid.request.company.name}
                          </Link>
                        ) : 'N/A'}
                      </td>
                      <td>
                        {bid.bank?.name ? (
                          <Link to={`/admin/organizations/${bid.bank._id || bid.bank.id}`}>
                            {bid.bank.name}
                          </Link>
                        ) : 'N/A'}
                      </td>
                      <td>{formatCurrency(bid.amount, bid.currency)}</td>
                      <td>{bid.interestRate || 0}%</td>
                      <td>{bid.createdAt ? new Date(bid.createdAt).toLocaleDateString() : 'N/A'}</td>
                      <td>
                        <span className={`status status-${bid.status === 'approved' ? 'approved' : bid.status === 'rejected' ? 'rejected' : bid.status === 'cancelled' ? 'cancelled' : 'pending'}`}>
                          {bid.status || 'pending'}
                        </span>
                      </td>
                      <td>
                        <Link
                          to={`/admin/bids/${bid.id || bid._id}`}
                          className="admin-view-btn"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="9">No bids found for this filter</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BidsManagement;